import React, { useState, useEffect, useRef, useCallback } from "react";
import { observer } from "mobx-react";
import { Box, Paper, Typography, IconButton, Collapse, Chip, Divider, Stack, useMediaQuery, useTheme, Button } from "@mui/material";
import {
  BugReport as BugReportIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import useStore from "../../helpers/useStore";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import { WebSocketContext } from "../../stores/WebSocket/WebSocket.provider";
import { useNavTablesStore } from "../../stores/NavTables";
import { usePatientRoute } from "../../routes/usePatientRoute";
import useDebugUI from "../../hooks/useDebugUI";
import { AppointmentWebSocketEvent } from "../../stores/WebSocket/types";
import { useApi } from "../../api/ApiProvider";
import { useWorkflow } from "../../hooks/useWorkflow";
import { WorkflowResultDtoWorkflowNameEnum } from "../../api-client";
import { useLocationId } from "../../stores/User/User.hooks";

const DeviceSizeLogger = () => {
  const theme = useTheme();
  const matchesXS = useMediaQuery(theme.breakpoints.down("xs"));
  const matchesSM = useMediaQuery(theme.breakpoints.up("sm") && theme.breakpoints.down("md"));
  const matchesMD = useMediaQuery(theme.breakpoints.up("md") && theme.breakpoints.down("lg"));
  const matchesLG = useMediaQuery(theme.breakpoints.up("lg") && theme.breakpoints.down("xl"));
  const matchesXL = useMediaQuery(theme.breakpoints.up("xl"));

  React.useEffect(() => {
    // console.log("Current breakpoint:");
    if (matchesXS) console.log("XS");
    if (matchesSM) console.log("SM");
    if (matchesMD) console.log("MD");
    if (matchesLG) console.log("LG");
    if (matchesXL) console.log("XL");
  }, [matchesXS, matchesSM, matchesMD, matchesLG, matchesXL]);

  return (
    <div>
      Current screen size is:
      {matchesXS && "XS"}
      {matchesSM && "SM"}
      {matchesMD && "MD"}
      {matchesLG && "LG"}
      {matchesXL && "XL"}
    </div>
  );
};

// Formatting helpers (top-level so helper components can use them)
const formatValue = (value: any) => {
  if (value === null || value === undefined) return "null";
  if (typeof value === "string") return `"${value}"`;
  if (typeof value === "boolean") return value.toString();
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (typeof value === "object") return "Object";
  return String(value);
};

const getValueColor = (value: any) => {
  if (value === null || value === undefined) return "#999";
  if (typeof value === "string") return "#4CAF50";
  if (typeof value === "boolean") return "#FF9800";
  if (typeof value === "number") return "#2196F3";
  if (Array.isArray(value)) return "#9C27B0";
  return "#795548";
};

// Interface for tracking WebSocket messages
interface WebSocketMessage {
  id: string;
  timestamp: Date;
  type: 'received' | 'sent' | 'connection';
  event?: string;
  data?: any;
  source?: string;
  action?: string;
}

// Smart formatting for workflow messages
const DebugMessageData = ({ data, event }: { data: any; event?: string }) => {
  // workflowEventResult with LLM step data
  if (event === "workflowEventResult" && data?.eventName && data?.data) {
    const d = data.data;
    const stepName = d.stepName;
    const durationSec = d.durationMs ? `${(d.durationMs / 1000).toFixed(1)}s` : null;
    const model = d.llmModel;
    const day = d.day;
    const dayIndex = d.dayIndex;
    const totalDays = d.totalDays;
    const output = d.output;
    const error = d.errorMessage;
    const meta = d.metadata;

    return (
      <Box sx={{ pl: 1, mt: 0.25 }}>
        <Typography variant="caption" sx={{ display: "block", color: "#1565c0", fontWeight: "bold" }}>
          🤖 {data.eventName}: {stepName || "unknown"}
        </Typography>
        {day && (
          <Typography variant="caption" sx={{ display: "block", color: "#333" }}>
            📅 {day}{dayIndex && totalDays ? ` (${dayIndex}/${totalDays})` : ""}
            {durationSec && ` • ⏱ ${durationSec}`}
            {model && ` • 🧠 ${model}`}
          </Typography>
        )}
        {!day && durationSec && (
          <Typography variant="caption" sx={{ display: "block", color: "#333" }}>
            ⏱ {durationSec}{model && ` • 🧠 ${model}`}
          </Typography>
        )}
        {d.input && (
          <Typography variant="caption" sx={{ display: "block", color: "#555" }}>
            ➡️ {d.input}
          </Typography>
        )}
        {output && (
          <Typography variant="caption" sx={{ display: "block", color: "#2e7d32" }}>
            ✅ {typeof output === "string" ? output.substring(0, 120) : JSON.stringify(output).substring(0, 120)}
          </Typography>
        )}
        {error && (
          <Typography variant="caption" sx={{ display: "block", color: "#d32f2f" }}>
            ❌ {error.substring(0, 120)}
          </Typography>
        )}
        {meta && (
          <Typography variant="caption" sx={{ display: "block", color: "#666", fontSize: "9px" }}>
            {Object.entries(meta).filter(([, v]) => v != null).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(" • ")}
          </Typography>
        )}
      </Box>
    );
  }

  // workflowProgress — compact progress line
  if (event === "workflowProgress") {
    const pct = data.percent ?? 0;
    const msg = data.message;
    return (
      <Typography variant="caption" sx={{ display: "block", color: "#333", pl: 1 }}>
        📊 {pct}%{msg ? ` — ${msg}` : ""}
      </Typography>
    );
  }

  // workflowResult — summary of final result
  if (event === "workflowResult" && data?.data) {
    const result = data.data;
    const actionGroups = result?.ActionGroup || result?.day_outcomes;
    const summary = result?.summary;
    return (
      <Box sx={{ pl: 1, mt: 0.25 }}>
        <Typography variant="caption" sx={{ display: "block", color: "#1b5e20", fontWeight: "bold" }}>
          🏁 Result: {data.workflowName || "workflow"}
        </Typography>
        {actionGroups && (
          <Typography variant="caption" sx={{ display: "block", color: "#333" }}>
            {Array.isArray(actionGroups) ? `${actionGroups.length} day(s) processed` : ""}
          </Typography>
        )}
        {summary && (
          <Typography variant="caption" sx={{ display: "block", color: "#555", fontStyle: "italic" }}>
            "{summary.substring(0, 150)}{summary.length > 150 ? "..." : ""}"
          </Typography>
        )}
      </Box>
    );
  }

  // Default: raw JSON truncated
  const raw = JSON.stringify(data);
  return (
    <Typography variant="caption" sx={{ display: "block", color: "#333", wordBreak: "break-all" }}>
      Data: {raw.substring(0, 100)}{raw.length > 100 ? '...' : ''}
    </Typography>
  );
};

// Small helper components to make adding new sections trivial
interface SectionProps {
  title: string;
  color?: string;
  icon?: string; // optional emoji / char
  fullWidth?: boolean;
  children: React.ReactNode;
}

const Section = ({ title, color = "#1976d2", icon, fullWidth, children }: SectionProps) => (
  <Box
    sx={{
      flex: fullWidth ? "1 1 100%" : { xs: "1 1 100%", sm: "1 1 calc(50% - 16px)" },
      minWidth: 240,
      border: `1px solid ${color}33`,
      borderRadius: 1,
      p: 1.25,
      background: "rgba(255,255,255,0.6)",
      backdropFilter: "blur(4px)",
    }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, color }}>
      {icon && `${icon} `}{title}
    </Typography>
    {children}
  </Box>
);

interface SectionRowProps {
  label: string;
  value: any;
  valueColor?: string;
  raw?: boolean; // do not format value
}

const SectionRow = ({ label, value, valueColor, raw }: SectionRowProps) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, gap: 1 }}>
    <Typography variant="caption" sx={{ flex: 1, whiteSpace: "nowrap" }}>{label}:</Typography>
    <Chip
      label={raw ? value : formatValue(value)}
      size="small"
      sx={{
        fontSize: "10px",
        height: 20,
        ml: 1,
        color: valueColor || getValueColor(value),
        bgcolor: (theme) => `${valueColor || getValueColor(value)}14`,
        maxWidth: 160,
      }}
    />
  </Box>
);

const StoreDebugWindow = observer(() => {
  const { debugUI } = useDebugUI();
  const [isOpen, setIsOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [activeView, setActiveView] = useState<'monitor' | 'stores'>('monitor');
  
  // WebSocket message tracking
  const [webSocketMessages, setWebSocketMessages] = useState<WebSocketMessage[]>([]);
  const messageIdCounter = useRef(0);
  const maxMessages = 50; // Keep last 50 messages

  const PatientsStore = useStore(PatientsContext);
  const CalendarStore = useStore(CalendarContext);
  const WebSocketStore = useStore(WebSocketContext);
  const routeState = usePatientRoute();
  const { agentTools } = useApi();
  const locationId = useLocationId();

  // Backplanning workflow trigger
  const [backplanLoading, setBackplanLoading] = useState(false);
  const { subscribeToWorkflow } = useWorkflow({
    workflowName: "BACKPLANNING_WORKFLOW" as WorkflowResultDtoWorkflowNameEnum,
    onResult: () => setBackplanLoading(false),
    onError: () => setBackplanLoading(false),
  });

  const triggerBackplan = useCallback(async () => {
    try {
      setBackplanLoading(true);
      const dto: any = {
        locationId: locationId || "1",
        endDate: new Date().toISOString(),
      };
      console.log("[Backplan Debug] Triggering with:", dto);
      const job = await agentTools.scheduleBackplanningWorkflow(dto);
      if (job) subscribeToWorkflow(job);
    } catch (err: any) {
      setBackplanLoading(false);
      addMessage({ type: 'received', event: 'backplan-error', data: { error: err?.message || String(err) } });
    }
  }, [agentTools, locationId, subscribeToWorkflow]);

  // Try to get NavTables store if it's available (may not be wrapped in provider)
  let NavTablesStore = null;
  try {
    NavTablesStore = useNavTablesStore();
  } catch (error) {
    // NavTables store not available (not wrapped in provider)
    // console.debug("NavTables store not available:", error);
  }

  // Helper function to add a new message
  const addMessage = (message: Omit<WebSocketMessage, 'id' | 'timestamp'>) => {
    const newMessage: WebSocketMessage = {
      ...message,
      id: `msg-${++messageIdCounter.current}`,
      timestamp: new Date(),
    };
    
    setWebSocketMessages(prev => {
      const updated = [newMessage, ...prev];
      return updated.slice(0, maxMessages); // Keep only the last maxMessages
    });
  };

  // Clear messages
  const clearMessages = () => {
    setWebSocketMessages([]);
    messageIdCounter.current = 0;
  };

  // Set up WebSocket listeners
  useEffect(() => {
    if (!WebSocketStore) return;

    // Track connection events
    const onConnect = () => {
      addMessage({
        type: 'connection',
        event: 'connect',
        data: { socketId: WebSocketStore.socket?.id }
      });
    };

    const onDisconnect = () => {
      addMessage({
        type: 'connection',
        event: 'disconnect',
        data: {}
      });
    };

    const onConnectError = (error: Error) => {
      addMessage({
        type: 'connection',
        event: 'connect_error',
        data: { error: error.message }
      });
    };

    // Catch-all listener for debugging
    const onAnyEvent = (eventName: string, ...args: any[]) => {
      // Skip logging our own debug events and common internal events
      if (!['connect', 'disconnect', 'connect_error'].includes(eventName)) {
        addMessage({
          type: 'received',
          event: eventName,
          data: args.length === 1 ? args[0] : args,
          action: `Raw event: ${eventName}`
        });
      }
    };

    // Set up appointment event listener
    const unsubscribeAppointments = WebSocketStore.onAppointmentEvent((event: AppointmentWebSocketEvent) => {
      addMessage({
        type: 'received',
        event: 'appointmentEvent',
        data: event.data,
        source: event.source,
        action: `${event.action} ${event.type}`
      });
    });

    // Register all listeners
    WebSocketStore.socket.on('connect', onConnect);
    WebSocketStore.socket.on('disconnect', onDisconnect);
    WebSocketStore.socket.on('connect_error', onConnectError);
    
    // Add catch-all listener to see ALL incoming events
    WebSocketStore.socket.onAny(onAnyEvent);

    return () => {
      // Cleanup
      unsubscribeAppointments();
      WebSocketStore.socket.off('connect', onConnect);
      WebSocketStore.socket.off('disconnect', onDisconnect);
      WebSocketStore.socket.off('connect_error', onConnectError);
      WebSocketStore.socket.offAny(onAnyEvent);
    };
  }, [WebSocketStore]);

  if (!PatientsStore || !CalendarStore) return null;

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    const startX = e.clientX - position.x;
    const startY = e.clientY - position.y;

    const handleMouseMove = (e: MouseEvent) => {
      setPosition({
        x: e.clientX - startX,
        y: e.clientY - startY,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // (formatValue & getValueColor now top-level)

  if (!isOpen) {
    return (
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          left: 20,
          zIndex: 9999,
        }}
      >
        <IconButton
          onClick={() => setIsOpen(true)}
          sx={{
            backgroundColor: "#1976d2",
            color: "white",
            "&:hover": { backgroundColor: "#1565c0" },
            boxShadow: 3,
          }}
        >
          <BugReportIcon />
        </IconButton>
      </Box>
    );
  }

  // Don't render if debug UI is disabled
  if (!debugUI) {
    return null;
  }

  return (
    <Paper
      sx={{
        position: "fixed",
        left: position.x,
        top: position.y,
        width: 800, // doubled width for more sections side-by-side
        maxWidth: '95vw',
        maxHeight: 700,
        overflow: "auto",
        zIndex: 9999,
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px)",
        border: "2px solid #1976d2",
        cursor: isDragging ? "grabbing" : "grab",
        boxSizing: 'border-box'
      }}
      elevation={8}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1,
          backgroundColor: "#1976d2",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        onMouseDown={handleMouseDown}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BugReportIcon fontSize="small" />
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
            Store Debug
          </Typography>
        </Box>
        <Box>
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{ color: "white", mr: 0.5 }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ display: 'flex', fontSize: "12px" }}>
          {/* Sidebar Nav */}
          <Box
            sx={{
              width: 90,
              minWidth: 90,
              borderRight: '1px solid #e0e0e0',
              bgcolor: '#f5f5f5',
              display: 'flex',
              flexDirection: 'column',
              py: 1,
            }}
          >
            {[
              { key: 'monitor' as const, label: 'Monitor', icon: '🔌' },
              { key: 'stores' as const, label: 'Stores / Others', icon: '🏪' },
            ].map((item) => (
              <Box
                key={item.key}
                onClick={() => setActiveView(item.key)}
                sx={{
                  px: 1,
                  py: 1.25,
                  cursor: 'pointer',
                  textAlign: 'center',
                  bgcolor: activeView === item.key ? '#1976d2' : 'transparent',
                  color: activeView === item.key ? 'white' : '#555',
                  '&:hover': {
                    bgcolor: activeView === item.key ? '#1565c0' : '#e0e0e0',
                  },
                  transition: 'background-color 0.15s',
                }}
              >
                <Typography sx={{ fontSize: '16px', lineHeight: 1 }}>{item.icon}</Typography>
                <Typography sx={{ fontSize: '9px', fontWeight: activeView === item.key ? 'bold' : 'normal', mt: 0.25 }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Content Area */}
          <Box sx={{ flex: 1, p: 1.5, overflow: 'auto' }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              {/* ── Monitor View ── */}
              {activeView === 'monitor' && (
                <>
                  <Section title="WebSocket Monitor" icon="🔌" color="#9c27b0" fullWidth>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <SectionRow label="connected" value={WebSocketStore?.isConnected || false} />
                        <SectionRow label="socketId" value={WebSocketStore?.socket?.id || "none"} />
                        <SectionRow label="url" value={process.env.REACT_APP_API_URL || "undefined"} />
                        <SectionRow label="events" value={`Array(${WebSocketStore?.getRegisteredEvents?.()?.length || 0})`} raw valueColor="#9C27B0" />
                        {WebSocketStore?.connectionError && (
                          <SectionRow label="error" value={WebSocketStore.connectionError} valueColor="#f44336" />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Button
                          size="small"
                          onClick={clearMessages}
                          startIcon={<ClearIcon />}
                          sx={{ minWidth: 'auto', p: 0.5, fontSize: '10px' }}
                        >
                          Clear
                        </Button>
                        <Button
                          size="small"
                          onClick={() => WebSocketStore?.sendTestMessage?.()}
                          sx={{ minWidth: 'auto', p: 0.5, fontSize: '10px', bgcolor: '#e3f2fd' }}
                          disabled={!WebSocketStore?.isConnected}
                        >
                          Test
                        </Button>
                        <Button
                          size="small"
                          onClick={triggerBackplan}
                          sx={{ minWidth: 'auto', p: 0.5, fontSize: '10px', bgcolor: '#fce4ec' }}
                          disabled={backplanLoading}
                        >
                          {backplanLoading ? "Running..." : "Test Backplan"}
                        </Button>
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: "bold" }}>
                      Recent Messages ({webSocketMessages.length}/{maxMessages}):
                    </Typography>
                    <Box sx={{ maxHeight: 300, overflow: "auto", fontSize: "9px" }}>
                      {webSocketMessages.length === 0 ? (
                        <Typography variant="caption" sx={{ color: "#999", fontStyle: "italic" }}>
                          No messages yet - check browser console for detailed logs
                        </Typography>
                      ) : (
                        webSocketMessages.map((msg) => (
                          <Box
                            key={msg.id}
                            sx={{
                              mb: 0.5,
                              p: 0.5,
                              bgcolor: msg.type === 'connection' ? "#e3f2fd" : msg.type === 'received' ? "#f3e5f5" : "#fff3e0",
                              borderRadius: 0.5,
                              borderLeft: `3px solid ${
                                msg.type === 'connection' ? "#2196f3" : msg.type === 'received' ? "#9c27b0" : "#ff9800"
                              }`,
                            }}
                          >
                            <Typography variant="caption" sx={{ display: "block", fontWeight: "bold" }}>
                              {msg.type === 'connection' ? '🔌' : msg.type === 'received' ? '📥' : '📤'} {msg.timestamp.toLocaleTimeString()} - {msg.event}
                            </Typography>
                            {msg.action && (
                              <Typography variant="caption" sx={{ display: "block", color: "#666" }}>
                                Action: {msg.action}
                              </Typography>
                            )}
                            {msg.source && (
                              <Typography variant="caption" sx={{ display: "block", color: "#666" }}>
                                Source: {msg.source}
                              </Typography>
                            )}
                            {msg.data && Object.keys(msg.data).length > 0 && (
                              <DebugMessageData data={msg.data} event={msg.event} />
                            )}
                          </Box>
                        ))
                      )}
                    </Box>
                  </Section>

                  <Section title="Device" icon="💻">
                    <DeviceSizeLogger />
                  </Section>
                </>
              )}

              {/* ── Stores / Others View ── */}
              {activeView === 'stores' && (
                <>
                  {/* <Section title="URL Route State" icon="📍">
                    <SectionRow label="patientId" value={routeState.patientId} />
                    <SectionRow label="selectedTab" value={routeState.selectedTab} />
                    <SectionRow label="rxId" value={routeState.rxId} />
                  </Section> */}

                  <Section title="Experiments" icon="🧪" color="#7b1fa2">
                    {/* Shelf-in-drawer prototype: legacy band by default, flip
                        to render the planning shelf inside the patient drawer.
                        Backed by CalendarStore + localStorage, so it survives
                        a reload mid-test. */}
                    <Box
                      component="label"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", fontSize: "11px" }}
                    >
                      <input
                        type="checkbox"
                        checked={CalendarStore.shelfInDrawer}
                        onChange={(e) => CalendarStore.setShelfInDrawer(e.target.checked)}
                      />
                      Shelf im Patienten-Drawer (statt Band am Kalender)
                    </Box>
                  </Section>

                  <Section title="Calendar Store" icon="📅" color="#f57c00">
                    <SectionRow label="selectedDateStr" value={CalendarStore.selectedDateStr} />
                    <SectionRow label="selectedPatientId" value={CalendarStore.selectedPatientId} />
                    <SectionRow label="selectedPatientLastName" value={CalendarStore.selectedPatientLastName} />
                    <SectionRow label="targetPatientId" value={CalendarStore.targetPatientId} />
                    <SectionRow label="targetPatientLastName" value={CalendarStore.targetPatientLastName} />
                    <SectionRow label="isCancelledView" value={CalendarStore.isCancelledView} />
                    <SectionRow label="continuousView" value={CalendarStore.continuousView} />
                    <SectionRow label="groupCheck" value={CalendarStore.groupCheck} />
                    <SectionRow label="patientDrawerOpen" value={CalendarStore.patientDrawerOpen} />
                    <SectionRow label="selectedRxId" value={CalendarStore.selectedRxId}/>
                    <SectionRow label="caResponseDialogOpen" value={CalendarStore.caResponseDialogOpen} />
                    <SectionRow label="caResponseData" value={CalendarStore.caResponseData ? "Object" : null} valueColor={CalendarStore.caResponseData ? "#4CAF50" : "#999"} />
                    <SectionRow label="users" value={`Array(${CalendarStore.columnUserIds.length})`} raw valueColor="#9C27B0" />
                  </Section>

                  <Section title="Patient Store" icon="🏪">
                    <SectionRow label="selectedPatients" value={`Array(${PatientsStore.openPatients.length})`} raw valueColor="#9C27B0" />
                    <SectionRow label="selectedPatientId" value={PatientsStore.selectedPatientId} />
                    <SectionRow label="selectedSubTabStr" value={PatientsStore.selectedSubTabStr} />
                    <SectionRow label="selectedRxId" value={PatientsStore.selectedRxId} />
                  </Section>

                  {NavTablesStore && (
                    <Section title="NavTables Store" icon="🗂️" color="#4caf50">
                      <SectionRow label="patientNavSelectedIndex" value={NavTablesStore.patientNavTableSelectedIndex} />
                      <SectionRow label="searchQuery" value={NavTablesStore.patientNavTableParams.patientTableFilters.lastName} />
                      <SectionRow label="locationId" value={NavTablesStore.patientNavTableParams.patientTableFilters.location} />
                      <SectionRow label="page" value={NavTablesStore.patientNavTableParams.patientTableFilters.page} />
                    </Section>
                  )}

                  {PatientsStore.openPatients.length > 0 && (
                    <Section
                      title={`Selected Patients (${PatientsStore.openPatients.length})`}
                      icon="👥"
                      fullWidth
                    >
                      <Stack spacing={1} sx={{ mb: 1 }}>
                        {PatientsStore.openPatients.map((patient: any, index: number) => (
                          <Box
                            key={patient.id || index}
                            sx={{ p: 1, bgcolor: "rgba(0,0,0,0.05)", borderRadius: 1 }}
                          >
                            <Typography variant="caption" sx={{ display: "block", fontWeight: "bold" }}>
                              Patient {index + 1}:
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block" }}>
                              ID: {patient.id} | {patient.lastName}, {patient.firstName}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Section>
                  )}
{/* 
                  <Section
                    title={`Routing State (${PatientsStore.selectedPatientsRoute.length})`}
                    icon="🛣️"
                    fullWidth
                  >
                    {PatientsStore.selectedPatientsRoute.length === 0 ? (
                      <Typography variant="caption" sx={{ color: "#999", fontStyle: "italic" }}>
                        No routing state
                      </Typography>
                    ) : (
                      PatientsStore.selectedPatientsRoute.map((route: any, index: number) => (
                        <Box
                          key={route.pId || index}
                          sx={{ mb: 1, p: 1, bgcolor: "rgba(0,0,0,0.05)", borderRadius: 1 }}
                        >
                          <Typography variant="caption" sx={{ display: "block", fontWeight: "bold" }}>
                            Route {index + 1}: pId={route.pId}
                          </Typography>
                          {route.rxId && (
                            <Typography variant="caption" sx={{ display: "block" }}>
                              rxId: {route.rxId}
                            </Typography>
                          )}
                          {route.doctorReportId && (
                            <Typography variant="caption" sx={{ display: "block" }}>
                              doctorReportId: {route.doctorReportId}
                            </Typography>
                          )}
                        </Box>
                      ))
                    )}
                  </Section> */}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
});

export default StoreDebugWindow;
